// Getting around: the Grand Loop railway through all six lands, the cable
// car up the High Peaks and the balloons over the warm sea.
//
// Every vehicle runs on the clock alone: where a
// train is, is a pure function of the time (a timetable of dwells and runs
// between stops, repeated forever), so every client draws the same train in
// the same place and nothing about it is ever broadcast. A rider only says
// which vehicle they're on (the `ride` tag on their move frame); everyone
// else seats them in it.
//
// buildTransport returns the lines, the stations' sprites and footprints,
// `near` (the corridor growing things keep clear of), `paintGround` (rails,
// sleepers, the viaduct, platforms) and the vehicles' painters.
import { inkPoly, inkRect, inkEllipse, line, roundRect, softShadow, mix } from "./paint"

const TAU = Math.PI * 2
const ACCEL = 0.2 // the share of a run spent speeding up, and again slowing down
const HAND = '"Bradley Hand", "Segoe Print", "Chalkboard SE", "Comic Neue", cursive'

// A smooth path through points (Catmull-Rom), as dense samples with their
// running length, so anything can be placed by its distance along it.
function smoothPath(points, closed, step = 8) {
    const n = points.length
    const P = (i) => (closed ? points[(i + n) % n] : points[Math.max(0, Math.min(n - 1, i))])
    const xs = [], ys = []
    const segs = closed ? n : n - 1
    for (let i = 0; i < segs; i++) {
        const p0 = P(i - 1), p1 = P(i), p2 = P(i + 1), p3 = P(i + 2)
        const k = Math.max(2, Math.ceil(Math.hypot(p2[0] - p1[0], p2[1] - p1[1]) / step))
        for (let j = 0; j < k; j++) {
            const t = j / k, t2 = t * t, t3 = t2 * t
            const cr = (a, b, c, d) => 0.5 * (2 * b + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3)
            xs.push(cr(p0[0], p1[0], p2[0], p3[0]))
            ys.push(cr(p0[1], p1[1], p2[1], p3[1]))
        }
    }
    if (!closed) { xs.push(points[n - 1][0]); ys.push(points[n - 1][1]) }
    return makePath(xs, ys, closed)
}

function makePath(xs, ys, closed) {
    const m = xs.length
    const cum = new Float64Array(m + (closed ? 1 : 0))
    for (let i = 1; i < cum.length; i++) { const a = i - 1, b = i % m; cum[i] = cum[i - 1] + Math.hypot(xs[b] - xs[a], ys[b] - ys[a]) }
    const L = cum[cum.length - 1]
    const wrap = (s) => (closed ? ((s % L) + L) % L : Math.max(0, Math.min(L, s)))
    function at(s) {
        s = wrap(s)
        let lo = 0, hi = cum.length - 1
        while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (cum[mid] <= s) lo = mid; else hi = mid }
        const a = lo, b = hi % m, u = (s - cum[lo]) / ((cum[hi] - cum[lo]) || 1)
        const dx = xs[b] - xs[a], dy = ys[b] - ys[a]
        return { x: xs[a] + dx * u, y: ys[a] + dy * u, a: Math.atan2(dy, dx) }
    }
    function nearest(x, y) {
        let best = 0, bd = Infinity
        for (let i = 0; i < m; i++) { const d = (xs[i] - x) ** 2 + (ys[i] - y) ** 2; if (d < bd) { bd = d; best = i } }
        return { s: cum[best], d: Math.sqrt(bd) }
    }
    // the right-hand normal at sample i (x right, y down)
    function normal(i) {
        const a = closed ? (i - 1 + m) % m : Math.max(0, i - 1), b = closed ? (i + 1) % m : Math.min(m - 1, i + 1)
        const dx = xs[b] - xs[a], dy = ys[b] - ys[a], len = Math.hypot(dx, dy) || 1
        return [-dy / len, dx / len]
    }
    function offset(d) {
        const ox = new Array(m), oy = new Array(m)
        for (let i = 0; i < m; i++) { const [nx, ny] = normal(i); ox[i] = xs[i] + nx * d; oy[i] = ys[i] + ny * d }
        return makePath(ox, oy, closed)
    }
    const reversed = () => makePath(xs.slice().reverse(), ys.slice().reverse(), closed)
    return { xs, ys, cum, L, m, closed, at, nearest, normal, offset, reversed }
}

// Distance covered over a run, 0..1 of the way for 0..1 of its time: speed
// up, cruise, slow down.
function ease(u) {
    const a = ACCEL, vm = 1 / (1 - a)
    if (u < a) return (vm * u * u) / (2 * a)
    if (u < 1 - a) return vm * (a / 2 + (u - a))
    return 1 - (vm * (1 - u) * (1 - u)) / (2 * a)
}

// The timetable: dwell at a stop, run to the next, over and over. A loop
// goes round; a shuttle goes to the end of the line and comes back.
function timetable(stopS, L, { loop, speed, dwell }) {
    const legs = []
    let t = 0
    const run = (s0, s1, next) => { const T = Math.abs(s1 - s0) / (speed * (1 - ACCEL)); legs.push({ t0: t, t1: t + T, stop: -1, next, s0, s1, dir: Math.sign(s1 - s0) }); t += T }
    const stay = (i, next, dir) => { legs.push({ t0: t, t1: t + dwell, stop: i, next, s0: stopS[i], s1: stopS[i], dir }); t += dwell }
    const n = stopS.length
    if (loop) {
        for (let i = 0; i < n; i++) { stay(i, (i + 1) % n, 1); run(stopS[i], i === n - 1 ? stopS[0] + L : stopS[i + 1], (i + 1) % n) }
    } else {
        for (let i = 0; i < n - 1; i++) { stay(i, i + 1, 1); run(stopS[i], stopS[i + 1], i + 1) }
        for (let i = n - 1; i > 0; i--) { stay(i, i - 1, -1); run(stopS[i], stopS[i - 1], i - 1) }
    }
    return { legs, cycle: t }
}

const hash = (str) => { let h = 2166136261; for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619) } return h >>> 0 }

// Which look a station takes, from the land it stands in.
const STYLE_BY_BIOME = { snow: "falu", birch: "falu", alpine: "stone", crag: "stone", "peak-snow": "stone", rainforest: "jungle", desert: "adobe", oasis: "adobe", canyon: "adobe", tropical: "thatch" }

export function buildTransport(config, { byKey, inSeaPolygon, islandAt, inRiver, onGlacier = () => false, nearPath, biome }) {
    const defs = config.transport || []
    const out = { active: defs.length > 0, tracks: [], byKey: {}, stations: {}, sprites: [], footprints: [], lights: [], anchors: [] }
    if (!out.active) return finish()

    defs.forEach((def) => {
        const stationKeys = def.points.filter((p) => typeof p === "string" && byKey[p])
        // a cabin hangs from the station's bullwheel, behind where you board
        const resolve = (p) => (typeof p === "string" ? (byKey[p] ? [byKey[p].x, byKey[p].y - (def.kind === "cable" ? 56 : 0)] : null) : p)
        const pts = def.points.map(resolve).filter(Boolean)
        if (pts.length < 2 || stationKeys.length < 2) return
        const center = smoothPath(pts, !!def.loop, def.kind === "train" ? 8 : 12)
        const paths = def.twin ? [[def.key, center.offset(def.twin / 2)], [`${def.key}_back`, center.offset(-def.twin / 2).reversed()]] : [[def.key, center]]
        paths.forEach(([key, path], ti) => {
            const stops = stationKeys.map((k) => {
                const l = byKey[k]
                return { key: k, s: path.nearest(l.x, l.y - (def.kind === "cable" ? 56 : 0)).s }
            }).sort((a, b) => a.s - b.s)
            const table = timetable(stops.map((s) => s.s), path.L, { loop: !!def.loop, speed: def.speed || 400, dwell: def.dwell || 6 })
            const track = { key, def, kind: def.kind, name: def.name, path, center, stops, ...table, count: def.vehicles || 1, back: ti === 1 }
            out.tracks.push(track)
            out.byKey[key] = track
        })
        stationKeys.forEach((k) => { out.stations[k] = { key: k, line: def.key, kind: def.kind, label: byKey[k].label } })
        // the stations' buildings, set beside their line
        if (def.kind === "train") stationKeys.forEach((k) => trainStation(byKey[k], center, def))
        if (def.kind === "cable") cableCar(stationKeys.map((k) => byKey[k]), center, def)
        if (def.kind === "balloon") stationKeys.forEach((k) => balloonField(byKey[k]))
    })

    // --- where a vehicle is --------------------------------------------------------

    function state(track, i, T) {
        const { legs, cycle } = track
        const tt = ((((T + (i * cycle) / track.count) % cycle) + cycle) % cycle)
        let lo = 0, hi = legs.length - 1
        while (lo < hi) { const mid = (lo + hi + 1) >> 1; if (legs[mid].t0 <= tt) lo = mid; else hi = mid - 1 }
        const leg = legs[lo]
        const u = Math.min(1, (tt - leg.t0) / ((leg.t1 - leg.t0) || 1))
        const s = leg.stop >= 0 ? leg.s0 : leg.s0 + (leg.s1 - leg.s0) * ease(u)
        return { s, stop: leg.stop, next: leg.next, left: leg.t1 - tt, dir: leg.dir || 1, u, leg }
    }

    // Where each car of vehicle i is: the front first, the rest behind it.
    function cars(track, i, T) {
        const st = state(track, i, T)
        const n = track.kind === "train" ? 3 : 1
        const gap = 60
        return Array.from({ length: n }, (_, k) => {
            const p = track.path.at(st.s - k * gap * st.dir)
            return { x: p.x, y: p.y, a: st.dir < 0 ? p.a + Math.PI : p.a, k, st }
        })
    }

    // How high a cabin or a balloon is over the ground, `s` along the way.
    function lift(track, s) {
        const L = track.path.L
        if (track.kind === "cable") return cableHeight(track, s) - 34
        if (track.kind === "balloon") {
            const sm = (x) => { const t = Math.max(0, Math.min(1, x)); return t * t * (3 - 2 * t) }
            return 8 + 230 * Math.min(sm(s / 520), sm((L - s) / 520))
        }
        return 0
    }
    function cableHeight(track, s) {
        const sup = track.supports
        if (!sup) return 60
        for (let k = 0; k < sup.length - 1; k++) {
            const a = sup[k], b = sup[k + 1]
            if (s >= a.s && s <= b.s) {
                const u = (s - a.s) / ((b.s - a.s) || 1)
                return a.h + (b.h - a.h) * u - (b.s - a.s) * 0.05 * 4 * u * (1 - u)
            }
        }
        return sup[s < sup[0].s ? 0 : sup.length - 1].h
    }

    // A rider's seat in the vehicle their tag names: "loop:1" is the Grand
    // Loop's second train. The id spreads riders over the seats.
    function seat(tag, id, T) {
        const [key, iStr] = String(tag).split(":")
        const track = out.byKey[key], i = parseInt(iStr, 10)
        if (!track || !(i >= 0 && i < track.count)) return null
        const h = hash(String(id))
        if (track.kind === "train") {
            const car = cars(track, i, T)[1 + (h % 2)]
            const along = (((h >> 3) % 3) - 1) * 12
            return { x: car.x + Math.cos(car.a) * along, y: car.y + Math.sin(car.a) * along, lift: 0, kind: "train", car: car.k, vehicle: `${key}:${i}` }
        }
        const st = state(track, i, T), p = track.path.at(st.s)
        return { x: p.x + ((h % 3) - 1) * 7, y: p.y, lift: lift(track, st.s), kind: track.kind, vehicle: `${key}:${i}` }
    }

    // The quickest way from one station to another: which track (for the
    // Loop, which way round), and which stops.
    function plan(fromKey, toKey) {
        let best = null
        out.tracks.forEach((track) => {
            const from = track.stops.findIndex((s) => s.key === fromKey), to = track.stops.findIndex((s) => s.key === toKey)
            if (from < 0 || to < 0 || from === to) return
            const a = track.stops[from].s, b = track.stops[to].s
            const dist = track.def.loop ? (((b - a) % track.path.L) + track.path.L) % track.path.L : Math.abs(b - a)
            if (!best || dist < best.dist) best = { track: track.key, from, to, dir: track.def.loop ? 1 : Math.sign(b - a), dist }
        })
        return best
    }

    // Seconds until a vehicle is standing at `stop` heading `dir`, and which one.
    function nextDeparture(trackKey, stop, dir, T) {
        const track = out.byKey[trackKey]
        let best = { wait: Infinity, i: -1 }
        if (!track) return best
        for (let i = 0; i < track.count; i++) {
            const tt = ((((T + (i * track.cycle) / track.count) % track.cycle) + track.cycle) % track.cycle)
            track.legs.forEach((leg) => {
                if (leg.stop !== stop || (dir && leg.dir !== dir)) return
                const wait = tt >= leg.t0 && tt < leg.t1 - 0.6 ? 0 : (((leg.t0 - tt) % track.cycle) + track.cycle) % track.cycle
                if (wait < best.wait) best = { wait, i }
            })
        }
        return best
    }

    // --- the corridor nothing grows in ---------------------------------------------
    const cell = 64, grid = new Map()
    const put = (x, y, r) => {
        for (let cx = Math.floor((x - r) / cell); cx <= Math.floor((x + r) / cell); cx++) {
            for (let cy = Math.floor((y - r) / cell); cy <= Math.floor((y + r) / cell); cy++) {
                const k = cx * 100003 + cy
                if (!grid.has(k)) grid.set(k, [])
                grid.get(k).push({ x, y, r })
            }
        }
    }
    out.tracks.forEach((track) => {
        if (track.back || track.kind !== "train") return
        const c = track.center
        for (let s = 0; s < c.L; s += 14) { const p = c.at(s); put(p.x, p.y, (track.def.twin || 0) / 2 + 16) }
    })
    out.anchors.forEach((a) => put(a.x, a.y, a.r))
    function near(x, y, pad) {
        for (let cx = Math.floor((x - pad) / cell); cx <= Math.floor((x + pad) / cell); cx++) {
            for (let cy = Math.floor((y - pad) / cell); cy <= Math.floor((y + pad) / cell); cy++) {
                const b = grid.get(cx * 100003 + cy)
                if (b) for (let i = 0; i < b.length; i++) if (Math.hypot(b[i].x - x, b[i].y - y) < b[i].r + pad) return true
            }
        }
        return false
    }

    // --- the stations ----------------------------------------------------------------

    function trainStation(l, center, def) {
        const st = center.nearest(l.x, l.y)
        const p = center.at(st.s)
        const [nx, ny] = [-Math.sin(p.a), Math.cos(p.a)]
        // the building stands on the far side from the viewer where it can,
        // so its front and canopy face the platform
        const side = l.side || (ny < -0.2 ? 1 : ny > 0.2 ? -1 : nx < 0 ? 1 : -1)
        const half = (def.twin || 0) / 2
        const plat = half + 22
        const px = p.x + nx * side * plat, py = p.y + ny * side * plat
        // step the house back from the line until its whole front clears the platform
        const clear = (x, y) => { for (let dx = -64; dx <= 64; dx += 16) for (const dy of [-20, 8]) if (center.nearest(x + dx, y + dy).d < half + 50) return false; return true }
        let far = half + 56
        while (far < half + 260 && !clear(p.x + nx * side * far, p.y + ny * side * far)) far += 8
        const bx = p.x + nx * side * far, by = p.y + ny * side * far + 8
        l.track = { x: p.x, y: p.y }
        l.x = Math.round(px); l.y = Math.round(py)
        l.platform = { s: st.s, side, from: half + 10, to: half + 42, len: 130 }
        const style = STYLE_BY_BIOME[biome(bx, by)] || "brick"
        out.sprites.push({ x: bx, y: by, box: [-74, -128, 74, 8], kind: "built", paint(ctx, pal) { paintStation(ctx, pal, style, l.label) } })
        out.footprints.push({ x: bx - 58, y: by - 34, w: 116, h: 36 })
        out.lights.push({ x: bx - 40, y: by - 50, r: 70 }, { x: bx + 40, y: by - 50, r: 70 })
        out.anchors.push({ x: bx, y: by - 20, r: 90 }, { x: px, y: py, r: 60 })
    }

    function cableCar(stations, center, def) {
        const track = out.tracks.find((t) => t.def === def)
        const L = center.L
        const count = def.pylons || 2
        const supports = [{ s: 0, h: 58 }]
        for (let k = 1; k <= count; k++) supports.push({ s: (L * k) / (count + 1), h: 132 })
        supports.push({ s: L, h: 58 })
        track.supports = supports
        supports.forEach((sp, k) => {
            const p = center.at(sp.s)
            if (k === 0 || k === supports.length - 1) {
                const l = stations[k === 0 ? 0 : stations.length - 1]
                const summit = k !== 0
                out.sprites.push({ x: p.x, y: p.y + 6, box: [-70, -112, 70, 10], kind: "built", paint(ctx, pal) { paintCableHouse(ctx, pal, summit) } })
                out.footprints.push({ x: p.x - 50, y: p.y - 26, w: 100, h: 32 })
                out.lights.push({ x: p.x, y: p.y - 30, r: 70 })
                out.anchors.push({ x: p.x, y: p.y, r: 95 }, { x: l.x, y: l.y, r: 50 })
            } else {
                out.sprites.push({ x: p.x, y: p.y, box: [-22, -sp.h - 14, 22, 6], kind: "built", paint(ctx, pal) { paintPylon(ctx, pal, sp.h) } })
                out.footprints.push({ circle: true, x: p.x, y: p.y - 2, r: 10 })
                out.anchors.push({ x: p.x, y: p.y, r: 36 })
            }
        })
        out.cables = out.cables || []
        out.cables.push(track)
    }

    function balloonField(l) {
        out.anchors.push({ x: l.x, y: l.y, r: l.r })
        out.sprites.push({ x: l.x + 96, y: l.y - 34, box: [-12, -86, 40, 6], kind: "built", paint(ctx, pal) { paintWindsock(ctx, pal) } })
        out.footprints.push({ circle: true, x: l.x + 96, y: l.y - 36, r: 6 })
        out.fields = out.fields || []
        out.fields.push(l)
    }

    // --- the ground: ballast, sleepers, rails; the viaduct over the sea -------------

    function paintGround(ctx, pal, vis) {
        out.tracks.forEach((track) => {
            if (track.kind !== "train" || track.back) return
            paintLine(ctx, pal, vis, track)
        })
        ;(out.fields || []).forEach((l) => { if (vis(l.x, l.y, l.r + 20)) paintField(ctx, pal, l) })
    }

    function paintLine(ctx, pal, vis, track) {
        const c = track.center, half = (track.def.twin || 0) / 2
        const rails = [track.path, out.byKey[`${track.key}_back`]?.path].filter(Boolean)
        const step = 6
        const n = Math.ceil(c.L / step)
        // what the line crosses, worked out once (every tile paints from it)
        const where = track.where || (track.where = Array.from({ length: n + 1 }, (_, i) => {
            const p = c.at(i * step)
            const sea = inSeaPolygon(p.x, p.y) && !islandAt(p.x, p.y, -30)
            return { ...p, sea, river: !sea && (inRiver(p.x, p.y, 16) || onGlacier(p.x, p.y)), cross: !sea && nearPath(p.x, p.y, 18) }
        }))
        const visible = (p) => vis(p.x, p.y, 90)
        const W = half * 2 + 30
        // runs of the same kind of ground, stroked as one line each
        const runs = (test, fn) => {
            let run = []
            for (let i = 0; i <= n; i++) {
                const p = where[i]
                if (test(p) && visible(p)) run.push(p)
                else if (run.length) { fn(run); run = [] }
            }
            if (run.length) fn(run)
        }
        const stroke = (run, width, color, alpha = 1) => {
            if (run.length < 2) return
            ctx.save(); ctx.globalAlpha = alpha; ctx.strokeStyle = color; ctx.lineWidth = width; ctx.lineCap = "butt"; ctx.lineJoin = "round"
            ctx.beginPath(); run.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y))); ctx.stroke(); ctx.restore()
        }
        // the viaduct: piers and their shadows on the water, then the deck
        runs((p) => p.sea, (run) => {
            run.forEach((p, i) => {
                if (i % 14) return
                const nx = -Math.sin(p.a), ny = Math.cos(p.a), ax = Math.cos(p.a), ay = Math.sin(p.a)
                const pier = [[p.x + nx * (W / 2 + 6) - ax * 7, p.y + ny * (W / 2 + 6) - ay * 7], [p.x + nx * (W / 2 + 6) + ax * 7, p.y + ny * (W / 2 + 6) + ay * 7], [p.x - nx * (W / 2 + 6) + ax * 7, p.y - ny * (W / 2 + 6) + ay * 7], [p.x - nx * (W / 2 + 6) - ax * 7, p.y - ny * (W / 2 + 6) - ay * 7]]
                ctx.save(); ctx.globalAlpha = 0.22; ctx.fillStyle = `rgb(${pal.shadow})`
                ctx.beginPath(); pier.forEach(([x, y], k) => (k ? ctx.lineTo(x + 14, y + 26) : ctx.moveTo(x + 14, y + 26))); ctx.closePath(); ctx.fill(); ctx.restore()
                inkPoly(ctx, pier.map(([x, y]) => [x, y + 8]), pal.stoneDark, pal.ink, { line: 1.2 })
                ctx.save(); ctx.globalAlpha = 0.5; ctx.strokeStyle = pal.foam; ctx.lineWidth = 1.4
                ctx.beginPath(); ctx.ellipse(p.x, p.y + 12, W / 2 + 10, 5, p.a, 0, TAU); ctx.stroke(); ctx.restore()
            })
            stroke(run, W + 4, pal.ink, 0.9)
            stroke(run, W, pal.stone)
            stroke(run, W - 10, pal.stoneLight, 0.55)
        })
        // bridges over the rivers and the glacier: timber decks
        runs((p) => p.river, (run) => {
            const a = run[0], b = run[run.length - 1]
            const ext = (p, d) => ({ x: p.x + Math.cos(p.a) * d, y: p.y + Math.sin(p.a) * d })
            const full = [ext(a, -18), ...run, ext(b, 18)]
            stroke(full, W + 6, pal.ink, 0.9)
            stroke(full, W + 2, pal.woodDark)
            stroke(full, W - 6, pal.wood)
        })
        // the ballast on land, with a darker shoulder
        runs((p) => !p.sea && !p.river && !p.cross, (run) => {
            stroke(run, W + 6, pal.pathDark, 0.35)
            stroke(run, W, mix(pal.pebble, pal.stoneDark, 0.3), 0.92)
            stroke(run, W - 8, mix(pal.pebble, pal.stone, 0.5), 0.6)
        })
        // level crossings: boards across where a road or trail meets the line
        runs((p) => p.cross && !p.sea && !p.river, (run) => {
            stroke(run, W - 2, pal.woodLight, 0.95)
            run.forEach((p, i) => {
                if (i % 2) return
                const nx = -Math.sin(p.a), ny = Math.cos(p.a)
                line(ctx, [[p.x + nx * (W / 2 - 2), p.y + ny * (W / 2 - 2)], [p.x - nx * (W / 2 - 2), p.y - ny * (W / 2 - 2)]], pal.woodDark, 1, 0.6)
            })
        })
        // the platforms beside the stations
        Object.values(out.stations).forEach((stn) => {
            const l = byKey[stn.key]
            if (!l.platform || stn.line !== track.def.key || !vis(l.x, l.y, 200)) return
            const { s, side, from, to, len } = l.platform
            const edge = (d) => { const pts = []; for (let k = -len; k <= len; k += 10) { const p = c.at(s + k); pts.push([p.x - Math.sin(p.a) * side * d, p.y + Math.cos(p.a) * side * d]) } return pts }
            const inner = edge(from), outer = edge(to)
            inkPoly(ctx, [...inner, ...outer.reverse()], pal.stoneLight, pal.ink, { line: 1.4 })
            line(ctx, edge(from + 3), "#E8C04A", 2, 0.9)
            for (let k = -len + 10; k < len; k += 20) {
                const p = c.at(s + k), d0 = from + 6, d1 = to
                line(ctx, [[p.x - Math.sin(p.a) * side * d0, p.y + Math.cos(p.a) * side * d0], [p.x - Math.sin(p.a) * side * d1, p.y + Math.cos(p.a) * side * d1]], pal.stoneDark, 0.8, 0.5)
            }
        })
        // sleepers and rails, per track
        rails.forEach((path) => {
            const m = Math.ceil(path.L / 7)
            ctx.save(); ctx.strokeStyle = pal.woodDark; ctx.lineWidth = 3; ctx.globalAlpha = 0.85
            ctx.beginPath()
            for (let k = 0; k <= m; k++) {
                const p = path.at(k * 7)
                if (!vis(p.x, p.y, 20)) continue
                const nx = -Math.sin(p.a) * 8, ny = Math.cos(p.a) * 8
                ctx.moveTo(p.x + nx, p.y + ny); ctx.lineTo(p.x - nx, p.y - ny)
            }
            ctx.stroke(); ctx.restore()
            ;[-4, 4].forEach((d) => {
                const rail = []
                for (let k = 0; k <= Math.ceil(path.L / 8); k++) {
                    const p = path.at(k * 8)
                    if (!vis(p.x, p.y, 30)) { if (rail.length) { strokeRail(rail); rail.length = 0 } continue }
                    rail.push([p.x - Math.sin(p.a) * d, p.y + Math.cos(p.a) * d])
                }
                if (rail.length) strokeRail(rail)
            })
        })
        function strokeRail(pts) {
            if (pts.length < 2) return
            ctx.save(); ctx.lineJoin = "round"
            ctx.strokeStyle = pal.ink; ctx.lineWidth = 2.2; ctx.globalAlpha = 0.85
            ctx.beginPath(); pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.stroke()
            ctx.strokeStyle = pal.stoneLight; ctx.lineWidth = 0.9; ctx.globalAlpha = 0.9
            ctx.stroke(); ctx.restore()
        }
    }

    function paintField(ctx, pal, l) {
        ctx.save()
        ctx.globalAlpha = 0.5; ctx.fillStyle = pal.pathLight
        ctx.beginPath(); ctx.ellipse(l.x, l.y, l.r * 0.95, l.r * 0.55, 0, 0, TAU); ctx.fill()
        ctx.globalAlpha = 0.8; ctx.strokeStyle = pal.cloth; ctx.lineWidth = 4; ctx.setLineDash([14, 10])
        ctx.beginPath(); ctx.ellipse(l.x, l.y, l.r * 0.62, l.r * 0.34, 0, 0, TAU); ctx.stroke()
        ctx.setLineDash([]); ctx.restore()
        inkEllipse(ctx, l.x, l.y, 24, 12, pal.woodLight, pal.ink, 1.3)
        line(ctx, [[l.x - 24, l.y], [l.x + 24, l.y]], pal.woodDark, 1, 0.6)
    }

    // --- the vehicles --------------------------------------------------------------

    // Everything moving at ground level, for the depth-sorted scene: one item
    // per car, each carrying whoever sits in it.
    function sceneItems(T, view, riders, drawRider) {
        const items = []
        const inView = (x, y, pad = 120) => x > view.x - pad && x < view.x + view.w + pad && y > view.y - pad && y < view.y + view.h + pad
        out.tracks.forEach((track) => {
            if (track.kind !== "train") return
            for (let i = 0; i < track.count; i++) {
                const tag = `${track.key}:${i}`
                const list = cars(track, i, T)
                if (!list.some((c) => inView(c.x, c.y))) continue
                const aboard = riders.get(tag) || []
                list.forEach((car) => {
                    items.push({ y: car.y + 8, draw: (ctx, pal) => {
                        paintCar(ctx, pal, car, track)
                        aboard.forEach((p) => { if (p.seat && p.seat.car === car.k) drawRider(ctx, p, p.seat.x, p.seat.y, { scale: 0.68, lift: 6 }) })
                    } })
                })
            }
        })
        return items
    }

    // Everything in the air, over the scene: the cables and their cabins,
    // the balloons, the trains' smoke.
    function drawAir(ctx, pal, T, view, riders, drawRider) {
        const inView = (x, y, pad = 200) => x > view.x - pad && x < view.x + view.w + pad && y > view.y - pad && y < view.y + view.h + pad
        ;(out.cables || []).forEach((track) => {
            const c = track.path
            // the haul rope, pylon to pylon, hanging a little in each span
            ctx.save(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.3; ctx.globalAlpha = 0.85
            ;[-5, 5].forEach((d) => {
                ctx.beginPath()
                for (let s = 0; s <= c.L; s += 20) {
                    const p = c.at(s), h = cableHeight(track, s)
                    const x = p.x + d, y = p.y - h
                    s ? ctx.lineTo(x, y) : ctx.moveTo(x, y)
                }
                ctx.stroke()
            })
            ctx.restore()
            for (let i = 0; i < track.count; i++) {
                const st = state(track, i, T), p = c.at(st.s)
                if (!inView(p.x, p.y)) continue
                const h = lift(track, st.s)
                softShadow(ctx, p.x + h * 0.25, p.y + 4, 16, 6, 0.18 * Math.max(0.3, 1 - h / 200), pal.shadow)
                const aboard = riders.get(`${track.key}:${i}`) || []
                paintCabin(ctx, pal, p.x, p.y - h, i, aboard, drawRider, st.dir)
            }
        })
        out.tracks.forEach((track) => {
            if (track.kind !== "balloon") return
            for (let i = 0; i < track.count; i++) {
                const st = state(track, i, T), p = track.path.at(st.s)
                if (!inView(p.x, p.y, 320)) continue
                const h = lift(track, st.s) + (st.stop < 0 ? Math.sin(T * 0.9 + i) * 5 : 0)
                softShadow(ctx, p.x + h * 0.35, p.y + 4, 30 * Math.max(0.5, 1 - h / 500), 11, 0.2 * Math.max(0.35, 1 - h / 300), pal.shadow)
                const aboard = riders.get(`${track.key}:${i}`) || []
                paintBalloon(ctx, pal, p.x, p.y - h, i, aboard, drawRider, T)
            }
        })
    }

    // Warm windows after dark.
    function drawLights(ctx, pal, T, view, glow, light) {
        if (!glow || light.lamps < 0.3) return
        ctx.save(); ctx.globalCompositeOperation = "lighter"; ctx.globalAlpha = 0.5 * light.lamps
        out.tracks.forEach((track) => {
            if (track.kind === "balloon") return
            for (let i = 0; i < track.count; i++) {
                if (track.kind === "train") {
                    cars(track, i, T).forEach((car) => { if (car.x > view.x - 80 && car.x < view.x + view.w + 80 && car.y > view.y - 80 && car.y < view.y + view.h + 80) ctx.drawImage(glow, car.x - 40, car.y - 34, 80, 60) })
                } else {
                    const st = state(track, i, T), p = track.path.at(st.s), h = lift(track, st.s)
                    ctx.drawImage(glow, p.x - 30, p.y - h - 30, 60, 60)
                }
            }
        })
        ctx.restore()
    }

    // Puffs from each locomotive's chimney, kept here so every client (and
    // each redraw) sees the same trains smoking.
    const puffs = []
    let puffClock = 0
    function update(dt, T, view, reduceMotion) {
        if (reduceMotion) { puffs.length = 0; return }
        puffClock += dt
        for (let i = puffs.length - 1; i >= 0; i--) { const p = puffs[i]; p.age += dt; p.x += p.vx * dt; p.y += p.vy * dt; if (p.age > p.life) puffs.splice(i, 1) }
        if (puffClock < 0.16) return
        puffClock = 0
        out.tracks.forEach((track) => {
            if (track.kind !== "train") return
            for (let i = 0; i < track.count; i++) {
                const loco = cars(track, i, T)[0]
                if (loco.x < view.x - 200 || loco.x > view.x + view.w + 200 || loco.y < view.y - 200 || loco.y > view.y + view.h + 200) continue
                const cx = loco.x + Math.cos(loco.a) * 16, cy = loco.y + Math.sin(loco.a) * 16 - 16
                const moving = loco.st.stop < 0
                puffs.push({ x: cx, y: cy, vx: -Math.cos(loco.a) * (moving ? 40 : 4) + 6, vy: moving ? -18 : -30, age: 0, life: moving ? 1.6 : 1.8, s: moving ? 7 : 4 })
            }
        })
        if (puffs.length > 160) puffs.splice(0, puffs.length - 160)
    }
    function drawSmoke(ctx, pal) {
        ctx.save()
        puffs.forEach((p) => {
            const k = p.age / p.life
            ctx.globalAlpha = (1 - k) * (1 - k) * (pal.night ? 0.25 : 0.42)
            ctx.fillStyle = pal.night ? "#8A9096" : "#F4F2EC"
            ctx.beginPath(); ctx.arc(p.x, p.y, p.s + k * 14, 0, TAU); ctx.fill()
        })
        ctx.restore()
    }

    return finish()

    function finish() {
        return Object.assign(out, { state: out.active ? state : null, cars: out.active ? cars : null, seat: out.active ? seat : () => null, plan: out.active ? plan : () => null, nextDeparture: out.active ? nextDeparture : () => ({ wait: Infinity, i: -1 }), near: out.active ? near : () => false, paintGround: out.active ? paintGround : () => {}, sceneItems: out.active ? sceneItems : () => [], drawAir: out.active ? drawAir : () => {}, drawLights: out.active ? drawLights : () => {}, update: out.active ? update : () => {}, drawSmoke: out.active ? drawSmoke : () => {} })
    }
}

// --- painters ---------------------------------------------------------------------

// A car, seen from above and a little in front: its side face first (the
// body pushed down a few units), then its roof, turned to the track.
function paintCar(ctx, pal, car, track) {
    const loco = car.k === 0
    const len = loco ? 58 : 54, w = 22, depth = 8
    const body = loco ? pal.trainGreen : pal.trainRed, bodyDark = loco ? pal.trainGreenDark : pal.trainRedDark
    ctx.save()
    ctx.translate(car.x, car.y)
    softShadow(ctx, 6, 10, len * 0.6, w * 0.75, 0.24, pal.shadow)
    const rect = (dy, fill, ink, lw = 1.4) => {
        ctx.save(); ctx.translate(0, dy); ctx.rotate(car.a)
        roundRect(ctx, -len / 2, -w / 2, len, w, 6)
        ctx.fillStyle = fill; ctx.fill()
        if (ink) { ctx.strokeStyle = ink; ctx.lineWidth = lw; ctx.stroke() }
        ctx.restore()
    }
    rect(depth, bodyDark, pal.ink)
    rect(depth / 2, bodyDark)
    rect(0, body, pal.ink, 1.5)
    ctx.rotate(car.a)
    if (loco) {
        // the boiler, the cab, the chimney and a brass dome
        roundRect(ctx, -6, -8, 32, 16, 7); ctx.fillStyle = bodyDark; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.2; ctx.stroke()
        roundRect(ctx, -27, -10, 18, 20, 3); ctx.fillStyle = pal.trainRoof; ctx.fill(); ctx.stroke()
        inkEllipse(ctx, 6, 0, 4, 4, pal.flag[1], pal.ink, 1.1)
        inkEllipse(ctx, 18, 0, 5, 5, pal.ink, pal.ink, 1)
        inkEllipse(ctx, 18, 0, 2.6, 2.6, "#2A2A2A", null)
        inkRect(ctx, len / 2 - 3, -w / 2 + 2, 3, w - 4, pal.falu, pal.ink, { line: 0.9 })
    } else {
        // an open excursion car: benches inside the rim
        roundRect(ctx, -len / 2 + 4, -w / 2 + 4, len - 8, w - 8, 3); ctx.fillStyle = pal.woodDark; ctx.fill()
        for (let x = -len / 2 + 10; x < len / 2 - 6; x += 11) line(ctx, [[x, -w / 2 + 5], [x, w / 2 - 5]], pal.woodLight, 2.4, 0.9)
    }
    // the coupling to the car in front
    if (!loco) line(ctx, [[len / 2, 0], [len / 2 + 6, 0]], pal.ink, 2)
    ctx.restore()
}

function paintCabin(ctx, pal, x, y, i, aboard, drawRider) {
    // hanger up to the rope, then the cabin
    line(ctx, [[x, y - 26], [x, y - 8]], pal.ink, 1.6)
    inkEllipse(ctx, x, y - 26, 4, 3, pal.metal, pal.ink, 1)
    ctx.save()
    roundRect(ctx, x - 20, y - 8, 40, 34, 7); ctx.fillStyle = i % 2 ? pal.trainRed : pal.flag[1]; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.5; ctx.stroke()
    roundRect(ctx, x - 15, y - 3, 30, 16, 3); ctx.fillStyle = pal.window; ctx.fill(); ctx.stroke()
    ctx.restore()
    aboard.forEach((p) => drawRider(ctx, p, x + (p.seat ? p.seat.x - x : 0) * 0.4, y + 9, { scale: 0.55, lift: 0, clip: [x - 15, y - 3, 30, 16] }))
    inkRect(ctx, x - 21, y + 22, 42, 4, pal.metal, pal.ink, { line: 1 })
}

function paintBalloon(ctx, pal, x, y, i, aboard, drawRider, T) {
    const colors = i % 2 ? [pal.flag[0], pal.flag[1]] : [pal.flag[2], pal.flag[3]]
    const R = 36, top = y - 64
    // ropes from the envelope down to the basket
    ;[-12, 12].forEach((dx) => line(ctx, [[x + dx * 2, top + R * 0.7], [x + dx, y - 14]], pal.inkSoft, 1.1))
    // the envelope, in gores
    const env = new Path2D()
    env.ellipse(x, top, R, R * 1.1, 0, Math.PI * 0.95, Math.PI * 2.05); env.quadraticCurveTo(x + R * 0.6, top + R * 1.3, x + 10, top + R * 1.45); env.lineTo(x - 10, top + R * 1.45); env.quadraticCurveTo(x - R * 0.6, top + R * 1.3, x - R * 0.99, top + R * 0.12); env.closePath()
    ctx.save()
    ctx.fillStyle = colors[0]; ctx.fill(env)
    ctx.save(); ctx.clip(env)
    for (let k = -3; k <= 3; k += 2) { ctx.beginPath(); ctx.ellipse(x + k * 7, top, 5.5, R * 1.3, 0, 0, TAU); ctx.fillStyle = colors[1]; ctx.fill() }
    ctx.globalAlpha = 0.25; ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.ellipse(x - R * 0.35, top - R * 0.35, R * 0.22, R * 0.4, -0.4, 0, TAU); ctx.fill()
    ctx.restore()
    ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.6; ctx.stroke(env)
    ctx.restore()
    // the burner's flame when it's flying
    inkRect(ctx, x - 6, y - 22, 12, 6, pal.metal, pal.ink, { line: 1 })
    // the basket, with whoever's aboard looking over its rim
    aboard.forEach((p) => drawRider(ctx, p, x + ((p.seat ? p.seat.x - x : 0) * 0.8), y - 18, { scale: 0.72, lift: 0 }))
    inkRect(ctx, x - 13, y - 14, 26, 16, pal.wood, pal.ink, { line: 1.5 })
    for (let k = -8; k <= 8; k += 5) line(ctx, [[x + k, y - 13], [x + k, y + 1]], pal.woodDark, 0.9, 0.7)
    line(ctx, [[x - 13, y - 8], [x + 13, y - 8]], pal.woodDark, 0.9, 0.7)
}

function stationColors(pal, style) {
    switch (style) {
    case "falu": return { wall: pal.falu, shade: pal.faluDark, roof: pal.shingleDark, roofDark: pal.ink, trim: pal.cloth }
    case "stone": return { wall: pal.stone, shade: pal.stoneDark, roof: pal.slate || pal.cragDark, roofDark: pal.ink, trim: pal.woodDark }
    case "jungle": return { wall: pal.woodLight, shade: pal.wood, roof: pal.leafDark, roofDark: pal.leafShadow, trim: pal.woodDark }
    case "adobe": return { wall: pal.sandDark, shade: mix(pal.sandDark, pal.woodDark, 0.35), roof: pal.roofLight, roofDark: pal.roof, trim: pal.flag[2] }
    case "thatch": return { wall: pal.cloth, shade: pal.clothShade, roof: pal.sandDark, roofDark: pal.woodDark, trim: pal.flag[2] }
    default: return { wall: pal.wall, shade: pal.wallShade, roof: pal.roof, roofDark: pal.roofDark, trim: pal.timber }
    }
}

// A little station house: two windows, a door, a clock in the gable and a
// canopy out over the platform.
function paintStation(ctx, pal, style, label) {
    const ink = pal.ink, c = stationColors(pal, style)
    // side depth
    inkPoly(ctx, [[52, 0], [64, -8], [64, -62], [52, -54]], c.shade, ink)
    inkRect(ctx, -52, -54, 104, 54, c.wall, ink, { line: 1.8 })
    if (style === "falu" || style === "jungle") for (let x = -52 + 12; x < 52; x += 12) line(ctx, [[x, -54], [x, 0]], c.shade, 1, 0.5)
    if (style === "stone") for (let y = -46; y < 0; y += 9) for (let x = -52 + ((y / 9) % 2 ? 0 : 7); x < 52; x += 14) line(ctx, [[x, y], [x + 11, y]], c.shade, 0.8, 0.5)
    // windows either side of the door
    ;[-32, 32].forEach((wx) => {
        inkRect(ctx, wx - 10, -42, 20, 18, pal.night ? pal.windowLit : pal.window, ink, { line: 1.3 })
        line(ctx, [[wx, -42], [wx, -24]], ink, 0.9, 0.7)
        inkRect(ctx, wx - 12, -24, 24, 3, c.trim, ink, { line: 0.9 })
    })
    inkRect(ctx, -10, -40, 20, 40, pal.door, ink, { line: 1.4 })
    inkEllipse(ctx, 5, -19, 1.6, 1.6, pal.flag[1], null)
    // the roof: thatch, leaves or slates, with a gable holding the clock
    const roof = [[-66, -52], [66, -52], [76, -60], [48, -92], [-48, -92]]
    inkPoly(ctx, roof, c.roof, ink, { line: 1.8 })
    if (style === "thatch" || style === "jungle") for (let x = -60; x < 64; x += 8) line(ctx, [[x, -52], [x * 0.75, -90]], c.roofDark, 0.9, 0.4)
    else for (let y = -60; y > -90; y -= 8) line(ctx, [[-60 + (y + 52) * -0.3, y], [60 - (y + 52) * -0.35, y]], c.roofDark, 0.9, 0.35)
    inkPoly(ctx, [[-18, -84], [18, -84], [0, -118]], c.wall, ink, { line: 1.5 })
    inkEllipse(ctx, 0, -98, 8, 8, pal.cloth, ink, 1.3)
    line(ctx, [[0, -98], [0, -103]], ink, 1.2)
    line(ctx, [[0, -98], [4, -98]], ink, 1.2)
    // the canopy over the platform, on two posts
    inkPoly(ctx, [[-60, -52], [60, -52], [66, -40], [-66, -40]], c.roofDark, ink, { line: 1.4 })
    for (let x = -60; x < 60; x += 10) inkPoly(ctx, [[x, -40], [x + 10, -40], [x + 5, -35]], c.trim, ink, { line: 0.8 })
    ;[-56, 56].forEach((x) => { line(ctx, [[x, -40], [x, 6]], pal.woodDark, 3); line(ctx, [[x, -40], [x, 6]], ink, 0.8, 0.7) })
    // the station's name board
    roundRect(ctx, -34, -70, 68, 14, 3); ctx.fillStyle = pal.cloth; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.1; ctx.stroke()
    ctx.fillStyle = pal.night ? "#2E3431" : pal.label; ctx.font = `700 9px ${HAND}`; ctx.textAlign = "center"
    ctx.fillText(shortName(label), 0, -60)
}

const shortName = (label) => String(label || "").replace(/^The /, "").replace(/ (station|halt|junction)$/i, "").toUpperCase().slice(0, 14)

function paintCableHouse(ctx, pal, summit) {
    const ink = pal.ink
    inkPoly(ctx, [[48, 0], [60, -8], [60, -50], [48, -42]], summit ? pal.stoneDark : pal.woodDark, ink)
    inkRect(ctx, -48, -42, 96, 42, summit ? pal.stone : pal.wood, ink, { line: 1.8 })
    if (!summit) for (let x = -48 + 12; x < 48; x += 12) line(ctx, [[x, -42], [x, 0]], pal.woodDark, 1, 0.5)
    inkRect(ctx, -16, -32, 32, 32, pal.screen, ink, { line: 1.4 })
    // the bullwheel the rope turns round, on its frame
    line(ctx, [[-30, -42], [-8, -62]], pal.metal, 3)
    line(ctx, [[30, -42], [8, -62]], pal.metal, 3)
    inkEllipse(ctx, 0, -62, 26, 8, pal.metal, ink, 1.6)
    inkEllipse(ctx, 0, -62, 18, 5, summit ? pal.stoneLight : pal.woodLight, ink, 1.1)
    // a pitched roof, snow on the summit's
    inkPoly(ctx, [[-60, -40], [60, -40], [44, -54], [-44, -54]], summit ? pal.snow : pal.roof, ink, { line: 1.6 })
    roundRect(ctx, -30, -30, 60, 12, 3); ctx.fillStyle = pal.cloth; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1; ctx.stroke()
    ctx.fillStyle = pal.night ? "#2E3431" : pal.label; ctx.font = `700 8px ${HAND}`; ctx.textAlign = "center"
    ctx.fillText(summit ? "SUMMIT" : "CABLE CAR", 0, -21)
}

function paintPylon(ctx, pal, h) {
    const ink = pal.ink
    // a lattice tower tapering up to the crossarm
    line(ctx, [[-12, 0], [-4, -h]], pal.metal, 2.4)
    line(ctx, [[12, 0], [4, -h]], pal.metal, 2.4)
    for (let y = -10; y > -h + 6; y -= 16) {
        const t = -y / h, a = 12 - 8 * t, b = 12 - 8 * Math.min(1, (-y + 16) / h)
        line(ctx, [[-a, y], [b, y - 16]], pal.metal, 1, 0.8)
        line(ctx, [[a, y], [-b, y - 16]], pal.metal, 1, 0.8)
    }
    inkRect(ctx, -12, -h - 4, 24, 5, pal.metal, ink, { line: 1 })
    ;[-5, 5].forEach((x) => inkEllipse(ctx, x, -h - 4, 2.5, 2.5, pal.inkSoft, null))
}

function paintWindsock(ctx, pal) {
    line(ctx, [[0, 0], [0, -80]], pal.woodDark, 3)
    inkPoly(ctx, [[0, -80], [34, -76], [32, -68], [0, -68]], "#E9785B", pal.ink, { line: 1.1 })
    inkPoly(ctx, [[11, -79], [21, -78], [21, -70], [11, -69]], pal.cloth, null)
}
